import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Loader2, 
  Download, 
  FileText,
  AlertCircle,
  ExternalLink,
  File,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  downloadAndCreateBlobUrl,
  downloadFile,
  openInNewTab,
  isPdfFile,
  isImageFile,
  isOfficeFile,
  getFileTypeLabel,
  type SupportedBucket,
} from "@/lib/documentPreview";
import { supabase } from "@/integrations/supabase/client";
import { useDocumentAccessLog } from "@/hooks/useDocumentAccessLog";

const PdfViewer = lazy(() => import("@/components/ui/PdfViewer"));

interface DocumentItem {
  id?: string;
  file_name: string;
  file_path: string;
  mime_type: string;
}

interface DocumentPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: DocumentItem | null;
  bucketName: SupportedBucket;
  /** Document type for audit logging */
  documentType?: "employee" | "applicant";
  /** All documents for navigation dropdown */
  allDocuments?: DocumentItem[];
  /** Callback when user selects a different document */
  onDocumentChange?: (doc: DocumentItem) => void;
}

/**
 * Unified Document Preview Component
 * 
 * Cross-platform document preview for PDFs and images.
 * Uses blob URLs for reliable rendering across all browsers.
 */
export function DocumentPreview({
  open,
  onOpenChange,
  document,
  bucketName,
  documentType = "employee",
  allDocuments = [],
  onDocumentChange,
}: DocumentPreviewProps) {
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const { logPreviewOpened, logPreviewFailed, logDownloadRequested } = useDocumentAccessLog();

  const cleanup = useCallback(() => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
    setError(null);
  }, [blobUrl]);

  useEffect(() => {
    if (open && document) {
      loadPreview();
    } else {
      cleanup();
    }

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [open, document?.file_path]);

  const loadPreview = async () => {
    if (!document) return;

    setLoading(true);
    setError(null);

    try {
      const url = await downloadAndCreateBlobUrl(bucketName, document.file_path, document.mime_type);
      setBlobUrl(url);
      
      // DSGVO Audit-Log: Preview erfolgreich geöffnet
      if (document.id) {
        logPreviewOpened(document.id, documentType, "modal");
      }
    } catch (err: any) {
      console.error("Error loading preview:", err);
      setError(err.message || "Fehler beim Laden der Vorschau");
      
      // DSGVO Audit-Log: Preview fehlgeschlagen
      if (document.id) {
        logPreviewFailed(document.id, documentType, "modal");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!document) return;

    // DSGVO Audit-Log: Download angefordert
    if (document.id) {
      logDownloadRequested(document.id, documentType, "download_button");
    }

    try {
      await downloadFile(bucketName, document.file_path, document.file_name);
    } catch (err: any) {
      console.error("Error downloading:", err);
      toast.error("Fehler beim Herunterladen");
    }
  };

  const handleOpenInNewTab = () => {
    if (!document) return;
    openInNewTab(bucketName, document, documentType);
  };

  const isPdf = isPdfFile(document?.mime_type || "", document?.file_name);
  const isImage = isImageFile(document?.mime_type || "", document?.file_name);
  const isOffice = isOfficeFile(document?.mime_type || "", document?.file_name);
  const canPreview = isPdf || isImage || isOffice;

  // For Office files, we need a public URL for Google Docs Viewer
  const [officePublicUrl, setOfficePublicUrl] = useState<string | null>(null);

  useEffect(() => {
    if (open && document && isOfficeFile(document.mime_type, document.file_name)) {
      // Create a signed URL for Office preview
      supabase.storage
        .from(bucketName)
        .createSignedUrl(document.file_path, 3600)
        .then(({ data, error }) => {
          if (data?.signedUrl) {
            setOfficePublicUrl(data.signedUrl);
          }
        });
    } else {
      setOfficePublicUrl(null);
    }
  }, [open, document?.file_path]);

  const currentIndex = allDocuments.findIndex(d => d.file_path === document?.file_path);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < allDocuments.length - 1;
  const showNav = allDocuments.length > 1 && onDocumentChange;

  const goToPrev = () => {
    if (hasPrev && onDocumentChange) onDocumentChange(allDocuments[currentIndex - 1]);
  };
  const goToNext = () => {
    if (hasNext && onDocumentChange) onDocumentChange(allDocuments[currentIndex + 1]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl w-[95vw] max-h-[95vh] h-[95vh] flex flex-col p-0 [&>button]:z-50">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="flex flex-col gap-2">
            {/* Document selector row */}
            {showNav ? (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={goToPrev} disabled={!hasPrev}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Select
                  value={document?.file_path || ""}
                  onValueChange={(val) => {
                    const doc = allDocuments.find(d => d.file_path === val);
                    if (doc && onDocumentChange) onDocumentChange(doc);
                  }}
                >
                  <SelectTrigger className="flex-1 min-w-0 text-sm font-medium">
                    <SelectValue placeholder="Dokument wählen" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover z-50">
                    {allDocuments.map((d, i) => (
                      <SelectItem key={d.file_path} value={d.file_path}>
                        <span className="truncate">{d.file_name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={goToNext} disabled={!hasNext}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-5 w-5 shrink-0" />
                <span className="truncate text-sm sm:text-base">{document?.file_name || "Dokumentvorschau"}</span>
              </div>
            )}
            {/* Action buttons */}
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleDownload}
                disabled={loading}
              >
                <Download className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Herunterladen</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleOpenInNewTab}
                disabled={!document || loading}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Neuer Tab</span>
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto bg-muted/30 min-h-0">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-[60vh]">
              <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Dokument wird geladen...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-[60vh] text-muted-foreground px-4">
              <AlertCircle className="h-16 w-16 mb-4 text-destructive" />
              <p className="text-lg font-medium mb-2">Vorschau nicht verfügbar</p>
              <p className="text-sm mb-6 text-center max-w-md">{error}</p>
              <Button onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Datei herunterladen
              </Button>
            </div>
          ) : blobUrl ? (
            <>
              {isPdf && (
                <div className="w-full h-[calc(95vh-120px)]">
                  <Suspense fallback={
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                  }>
                    <PdfViewer url={blobUrl} className="h-full" />
                  </Suspense>
                </div>
              )}
              {isImage && (
                <div className="flex items-center justify-center p-4 min-h-[60vh]">
                  <img
                    src={blobUrl}
                    alt={document?.file_name}
                    className="max-w-full max-h-[calc(95vh-140px)] object-contain rounded-lg shadow-lg"
                  />
                </div>
              )}
              {isOffice && officePublicUrl && (
                <div className="w-full h-[calc(95vh-120px)]">
                  <iframe
                    src={`https://docs.google.com/gview?url=${encodeURIComponent(officePublicUrl)}&embedded=true`}
                    className="w-full h-full border-0"
                    title={document?.file_name}
                  />
                </div>
              )}
              {isOffice && !officePublicUrl && (
                <div className="flex items-center justify-center h-[60vh]">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              )}
              {!canPreview && (
                <div className="flex flex-col items-center justify-center h-[60vh] text-muted-foreground px-4">
                  <File className="h-20 w-20 mb-6" />
                  <h3 className="text-lg font-semibold mb-2">Vorschau nicht verfügbar</h3>
                  <p className="text-sm mb-2 text-center">
                    {getFileTypeLabel(document?.mime_type || "")} kann nicht im Browser angezeigt werden.
                  </p>
                  <p className="text-xs text-muted-foreground mb-6">
                    ({document?.mime_type || "Unbekannter Dateityp"})
                  </p>
                  <Button onClick={handleDownload}>
                    <Download className="h-4 w-4 mr-2" />
                    Datei herunterladen
                  </Button>
                </div>
              )}
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DocumentPreview;
